import React, { useState } from 'react';
import { Plus, Trash2, Upload, Edit3, UserPlus, Phone, Award, MapPin, Save } from 'lucide-react';
import api, { formatApiError } from '../../lib/api';
import { useData } from '../../context/DataContext';
import { useToast } from '../../hooks/use-toast';

const empty = {
  name_en: '', name_hi: '', experience: 5,
  specialization_en: '', specialization_hi: '',
  temple_en: '', temple_hi: '', phone: '', photo: '',
};

export default function PanditsTab() {
  const { pandits, pujas, refresh } = useData();
  const { toast } = useToast();
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [show, setShow] = useState(false);

  const openNew = () => { setEditingId(null); setForm(empty); setShow(true); window.scrollTo({ top: 300, behavior: 'smooth' }); };
  const openEdit = (p) => { setEditingId(p.id); setForm({ ...empty, ...p }); setShow(true); window.scrollTo({ top: 300, behavior: 'smooth' }); };

  const del = async (id) => {
    if (!window.confirm('Delete this Pandit Ji?')) return;
    try {
      await api.delete(`/api/pandits/${id}`);
      await refresh();
      toast({ title: 'Pandit Ji removed' });
    } catch (e) { toast({ title: formatApiError(e), variant: 'destructive' }); }
  };

  const upFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => setForm((p) => ({ ...p, photo: r.result }));
    r.readAsDataURL(f);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.put(`/api/pandits/${editingId}`, form);
      else await api.post('/api/pandits', form);
      await refresh();
      toast({ title: editingId ? 'Pandit Ji updated' : 'Pandit Ji added' });
      setShow(false);
    } catch (err) { toast({ title: formatApiError(err), variant: 'destructive' }); }
  };

  return (
    <>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <p className="text-sm text-nv-muted">Add Pandit Jis and assign them to specific pujas</p>
        <button data-testid="add-pandit-btn" onClick={openNew} className="btn-orange rounded-full px-4 md:px-5 py-2.5 font-semibold flex items-center gap-2 text-sm">
          <UserPlus className="w-4 h-4" /> Add Pandit Ji
        </button>
      </div>

      {show && (
        <form onSubmit={save} className="bg-white border border-orange-200 rounded-2xl p-4 md:p-6 mb-6 grid md:grid-cols-2 gap-4 shadow-md">
          <h3 className="md:col-span-2 text-lg font-bold text-orange-800">{editingId ? 'Edit Pandit Ji' : 'New Pandit Ji'}</h3>
          {[
            ['name_en', 'Name (English)'], ['name_hi', 'Name (Hindi)'],
            ['experience', 'Experience (years)'], ['phone', 'Phone'],
            ['temple_en', 'Home Temple (English)'], ['temple_hi', 'Home Temple (Hindi)'],
            ['specialization_en', 'Specialization (English)'], ['specialization_hi', 'Specialization (Hindi)'],
          ].map(([k, l]) => (
            <div key={k}>
              <label className="text-xs font-medium text-stone-600 mb-1 block">{l}</label>
              <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: k === 'experience' ? +e.target.value : e.target.value })} required={['name_en', 'phone'].includes(k)} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          ))}
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-stone-600 mb-1 block">Photo (URL or upload)</label>
            <div className="flex gap-2">
              <input value={form.photo} onChange={(e) => setForm({ ...form, photo: e.target.value })} placeholder="https://..." className="flex-1 border border-orange-200 rounded-lg px-3 py-2 text-sm" />
              <label className="px-3 py-2 rounded-lg border border-orange-300 text-orange-700 text-sm cursor-pointer hover:bg-orange-50 flex items-center gap-1">
                <Upload className="w-4 h-4" /> Upload
                <input type="file" accept="image/*" onChange={upFile} className="hidden" />
              </label>
            </div>
            {form.photo && <img src={form.photo} alt="" className="mt-2 w-24 h-24 rounded-full object-cover border-2 border-orange-200" />}
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-3 justify-end pt-3">
            <button type="button" onClick={() => setShow(false)} className="px-5 py-2 rounded-lg border border-stone-300 text-stone-700">Cancel</button>
            <button data-testid="save-pandit-btn" type="submit" className="btn-orange px-6 py-2 rounded-lg font-semibold flex items-center gap-2"><Save className="w-4 h-4" /> Save Pandit</button>
          </div>
        </form>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pandits-list">
        {pandits.map((pd) => {
          const assignedPujas = pujas.filter((p) => p.panditId === pd.id);
          return (
            <div key={pd.id} className="bg-white border border-nv-gold/20 rounded-2xl p-5 shadow-sm">
              <div className="flex items-start gap-3">
                {pd.photo ? (
                  <img src={pd.photo} alt={pd.name_en} className="w-16 h-16 rounded-full object-cover border-2 border-orange-200 shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white text-2xl shrink-0">🙏</div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-nv-wine text-sm truncate">{pd.name_en}</h4>
                  <p className="text-[11px] text-nv-muted truncate devnag">{pd.name_hi}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <Award className="w-3 h-3 text-orange-600" />
                    <span className="text-[11px] text-orange-700 font-semibold">{pd.experience}+ yrs</span>
                  </div>
                </div>
              </div>
              <div className="mt-3 space-y-1 text-[11px] text-nv-muted">
                <div className="flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5 text-stone-400 shrink-0" /> {pd.temple_en}</div>
                <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-stone-400" /> {pd.phone}</div>
                <p className="text-stone-500 italic line-clamp-2">{pd.specialization_en}</p>
              </div>
              <div className="mt-3 py-2 border-t border-orange-100">
                <p className="text-[10px] uppercase tracking-wider text-orange-700 font-bold mb-1">Assigned Pujas ({assignedPujas.length})</p>
                {assignedPujas.length === 0 ? (
                  <p className="text-[11px] text-stone-400 italic">No pujas assigned yet</p>
                ) : (
                  <ul className="text-[11px] text-stone-700 space-y-0.5">
                    {assignedPujas.slice(0, 3).map((p) => (<li key={p.id} className="truncate">• {p.title_en}</li>))}
                    {assignedPujas.length > 3 && (<li className="text-orange-600">+ {assignedPujas.length - 3} more</li>)}
                  </ul>
                )}
              </div>
              <div className="flex gap-2 mt-3">
                <button data-testid={`edit-pandit-${pd.id}`} onClick={() => openEdit(pd)} className="flex-1 border border-orange-300 text-orange-700 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 hover:bg-orange-50"><Edit3 className="w-3 h-3" /> Edit</button>
                <button data-testid={`delete-pandit-${pd.id}`} onClick={() => del(pd.id)} className="flex-1 border border-red-300 text-red-600 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 hover:bg-red-50"><Trash2 className="w-3 h-3" /> Delete</button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
